import { useState, useRef, useEffect } from "react";
import { ContentItem } from "./types";
import { ContentCard as ContentCardUI } from "@/components/ui/content-card";
import CardThumbnail from "./CardThumbnail";
import GridCardDeleteDialog from "./GridCardDeleteDialog";
import { Tag, X, Pin, PinOff, FileImage } from "lucide-react";
import ArchiveIcon from "@/components/icons/ArchiveIcon";
import { useContentTags } from "@/hooks/useContentTags";
import AddTagButton from "@/components/common/AddTagButton";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { useSelectionHandlers } from "@/hooks/useSelectionHandlers";
import SelectionCheckbox from "./SelectionCheckbox";
import { useContentSelection } from "@/contexts/selection/SelectionContext";
import { isHighlightedText, getContentUrl, getHighlightedText } from "./contentCardUtils";
import { Button } from "@/components/ui/button";
import ManualThumbnailDialog from "../ManualThumbnailDialog";
import CustomAddIcon from "@/components/icons/CustomAddIcon";
import { useIsMobile } from "@/hooks/use-mobile";

interface ListItemCardProps {
  item: ContentItem;
  onClick?: () => void;
  onEdit?: (id: string, currentTitle: string) => void;
  onDelete?: (id: string) => void;
  onArchive?: (id: string) => void;
  onPin?: (id: string) => void;
  onUnpin?: (id: string) => void;
  onTagAdd?: (id: string) => void;
  onTagRemove?: (id: string, tagId: string) => void;
  allContentIds?: string[];  
  selectable?: boolean;      
}

const ListItemCard = ({
  item,
  onClick,
  onEdit,
  onDelete,
  onArchive,
  onPin,
  onUnpin,
  onTagAdd,
  onTagRemove,
  allContentIds = [],
  selectable = false,
}: ListItemCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showThumbnailDialog, setShowThumbnailDialog] = useState(false);
  const [showTagSelectionDialog, setShowTagSelectionDialog] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { contentTags, addTag, removeTag } = useContentTags(item.id);
  const { handleContentDragStart, handleContentDragEnd } = useDragAndDrop();
  const { selectedIds } = useContentSelection();
  const isMobile = useIsMobile();
  
  // Initialize selection handlers
  const { selected, handleSelectionClick } = useSelectionHandlers({
    contentId: item.id,
    allContentIds: allContentIds,
    onClick: onClick
  });

  useEffect(() => {
    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    const currentRef = cardRef.current;
    if (currentRef) {
      currentRef.addEventListener("mouseleave", handleMouseLeave);
    }
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);
  
  // Detect if this is highlighted text
  const isHighlight = isHighlightedText(item);
  const highlightedText = getHighlightedText(item);
  
  const handleCardClick = (e?: React.MouseEvent) => {
    console.log(`[ListItemCard] handleCardClick called for item ${item.id}`, {
      hasOnClick: !!onClick,
      selectable,
      eventType: e?.type
    });
    
    if (selectable && e) {
      handleSelectionClick(e);
    } else if (onClick) {
      console.log(`[ListItemCard] Calling onClick for item ${item.id}`);
      onClick();
    } else {
      console.warn(`[ListItemCard] No onClick handler provided for item ${item.id}`);
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const targetUrl = getContentUrl(item);
    console.log(`[NAVIGATION DEBUG] ListItemCard - Thumbnail clicked for item ${item.id}, opening URL: ${targetUrl}`);
    window.open(targetUrl, "_blank", "noopener");
  };

  const handleDeleteRequest = () => {
    console.log(`[NAVIGATION DEBUG] ListItemCard - Delete requested for item ${item.id}`);
    setContentToDelete(item.id);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    console.log(`[NAVIGATION DEBUG] ListItemCard - Delete confirmed for item ${contentToDelete}`);
    
    if (contentToDelete && onDelete) {
      console.log(`[NAVIGATION DEBUG] ListItemCard - Executing delete for item ${contentToDelete}`);
      onDelete(contentToDelete);
    }
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    console.log(`[NAVIGATION DEBUG] ListItemCard - Delete dialog open state changing to: ${open}`);
    setShowDeleteDialog(open);
  };

  const handleRemoveTag = (e: React.MouseEvent, tagId: string) => {
    console.log(`[NAVIGATION DEBUG] ListItemCard - Remove tag ${tagId} for item ${item.id}`);
    e.stopPropagation();
    removeTag(tagId);
  };

  const handleAddTagClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`[NAVIGATION DEBUG] ListItemCard - Add tag button clicked for item ${item.id}`);
    setShowTagSelectionDialog(true);
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    console.log(`[PIN DEBUG] ListItemCard - Pin/Unpin clicked for item ${item.id}`);
    console.log(`[PIN DEBUG] ListItemCard - isPinned: ${item.pinned}, onPin: ${!!onPin}, onUnpin: ${!!onUnpin}`);
    
    if (item.pinned && onUnpin) {
      console.log(`[PIN DEBUG] ListItemCard - Calling onUnpin for item ${item.id}`);
      onUnpin(item.id);
    } else if (!item.pinned && onPin) {
      console.log(`[PIN DEBUG] ListItemCard - Calling onPin for item ${item.id}`);
      onPin(item.id);
    }
  };

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onArchive) {
      onArchive(item.id);
    }
  };

  const handleThumbnailDialogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowThumbnailDialog(true);
  };

  const handleDragStart = (e: React.DragEvent) => {
    console.log(`[DRAG DEBUG] ListItemCard - Drag started for item ${item.id}`);
    
    if (selected && selectedIds.length > 1) {
      const dragData = {
        contentIds: selectedIds,
        contentTitles: selectedIds.length > 1 ? `${selectedIds.length} items` : item.title,
        isMultiDrag: true
      };
      
      handleContentDragStart(e, dragData);
    } else {
      const dragData = {
        contentId: item.id,
        contentTitle: item.title,
        currentCollectionId: item.collection_id
      };
      
      handleContentDragStart(e, dragData);
    }
    
    if (e.dataTransfer.setDragImage) {
      const ghostElement = document.createElement('div');
      ghostElement.classList.add('bg-white', 'dark:bg-gray-800', 'p-2', 'rounded', 'shadow-md');
      
      if (selected && selectedIds.length > 1) {
        ghostElement.textContent = `Dragging ${selectedIds.length} items`;
      } else {
        ghostElement.textContent = item.title || 'Content';
      }
      
      ghostElement.style.width = '180px';
      ghostElement.style.opacity = '0.7';
      document.body.appendChild(ghostElement);
      e.dataTransfer.setDragImage(ghostElement, 90, 30);
      
      setTimeout(() => {
        document.body.removeChild(ghostElement);
      }, 100);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    handleContentDragEnd();
  };

  const cardColor = item.collection_color || "#6B7789";

  // Desktop Layout (modified tag positioning)
  const DesktopLayout = () => (
    <div className={`flex items-center rounded-lg overflow-hidden shadow-sm border border-border bg-card relative ${selected ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}>
      {/* EXACT 60px height container with all content */}
      <div className="flex items-center gap-3 h-[60px] pr-3 flex-1">
        {/* Thumbnail - 16:9 aspect ratio, with relative positioning for checkbox overlay */}
        <div className="flex-shrink-0 w-[107px] h-[60px] rounded-lg overflow-hidden cursor-pointer relative">
          {/* Selection checkbox - overlays on thumbnail */}
          {selectable && (isHovering || selected) && (
            <SelectionCheckbox 
              isSelected={selected} 
              onClick={(e) => {
                e.stopPropagation();
                handleSelectionClick(e);
              }}
              data-selection-checkbox="true"
              className="absolute top-2 left-2 z-30"
            />
          )}
          
          <div onClick={handleThumbnailClick}>
            <CardThumbnail 
              thumbnailUrl={item.thumbnail_url} 
              contentType={item.content_type}
              title={item.title}
              contentItem={item}
              aspectRatio={16/9}
            />
          </div>
        </div>
        
        {/* Content Info - Title, Description, and Tags side by side */}
        <div className="flex-1 min-w-0 flex items-center justify-between h-full py-1">
          {/* Left side: Title and Description - centered vertically */}
          <div 
            className="flex-1 cursor-pointer overflow-hidden min-w-0 mr-4 flex flex-col justify-center h-full" 
            onClick={(e) => {
              e.stopPropagation();
              console.log(`[ListItemCard] Content area clicked for item ${item.id}`);
              handleCardClick(e);
            }}
          >
            {/* Title */}
            <h3 className="font-medium text-sm line-clamp-1 leading-tight text-card-foreground mb-0.5">
              {item.title}
            </h3>
            
            {/* Description or highlighted text preview */}
            {isHighlight && highlightedText ? (
              <p className="text-xs text-muted-foreground line-clamp-1 leading-tight">
                "{highlightedText}"
              </p>
            ) : item.description ? (
              <p className="text-xs text-muted-foreground line-clamp-1 leading-tight">
                {item.description}
              </p>
            ) : (
              <div className="h-3"></div>
            )}
          </div>
          
          {/* Right side: Tags container with Add button at far right */}
          <div className="flex flex-col items-end justify-start h-full min-w-0 max-w-[300px]">
            {/* Tags container with right-to-left flow */}
            <div className="flex flex-col items-end gap-1 h-full justify-start">
              {/* First line: Add Tag button (always at far right) + some tags */}
              <div className="flex items-center gap-1 flex-wrap justify-end max-w-full flex-row-reverse">
                {/* Add Tag Button - always at far right (first in reverse order) */}
                <button
                  className="flex-shrink-0 p-0 h-6 w-6 flex items-center justify-center hover:bg-transparent"
                  onClick={handleAddTagClick}
                >
                  <CustomAddIcon 
                    size={20} 
                    variant="gray" 
                  />
                </button>
                
                {/* First few tags (will appear to the left of Add button) */}
                {contentTags.slice(0, 2).map(tag => (
                  <div 
                    key={tag.id} 
                    className="flex items-center rounded-full px-2 py-0.5 text-xs whitespace-nowrap flex-shrink-0"
                    style={{ 
                      backgroundColor: `${tag.color}20`,
                      borderColor: tag.color,
                      border: `1px solid ${tag.color}`
                    }}
                  >
                    <span className="mr-1">{tag.name}</span>
                    <button 
                      className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                      onClick={(e) => handleRemoveTag(e, tag.id)}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Second line: Remaining tags if any (also right-aligned) */}
              {contentTags.length > 2 && (
                <div className="flex flex-wrap justify-end gap-1 max-w-full flex-row-reverse">
                  {contentTags.slice(2).map(tag => (
                    <div 
                      key={tag.id} 
                      className="flex items-center rounded-full px-2 py-0.5 text-xs whitespace-nowrap flex-shrink-0"
                      style={{ 
                        backgroundColor: `${tag.color}20`,
                        borderColor: tag.color,
                        border: `1px solid ${tag.color}`
                      }}
                    >
                      <span className="mr-1">{tag.name}</span>
                      <button 
                        className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                        onClick={(e) => handleRemoveTag(e, tag.id)}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Buttons - Individual icons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Pin/Unpin Button */}
          {(onPin || onUnpin) && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handlePinClick}
            >
              {item.pinned ? (
                <PinOff className="h-4 w-4 text-gray-600" />
              ) : (
                <Pin className="h-4 w-4 text-gray-600" />
              )}
            </Button>
          )}
          
          {/* Thumbnail Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={handleThumbnailDialogClick}
          >
            <FileImage className="h-4 w-4 text-gray-600" />
          </Button>
          
          {/* Archive Button */}
          {onArchive && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handleArchiveClick}
            >
              <ArchiveIcon className="h-4 w-4 text-gray-600" size={16} />
            </Button>
          )}
          
          {/* Delete Button */}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRequest();
              }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </Button>
          )}
        </div>
      </div>
      
      {/* Vertical color indicator at far right */}
      <div 
        className="w-[4px] h-[60px] flex-shrink-0" 
        style={{ backgroundColor: cardColor }}
      />
    </div>
  );

  // Mobile/Tablet Layout with removed scroll indicators
  const MobileLayout = () => (
    <div className={`rounded-lg overflow-hidden shadow-sm border border-border bg-card relative ${selected ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}>
      <div className="flex h-[60px] relative">
        {/* Horizontally scrollable container - removed scroll indicators */}
        <div 
          className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory flex-1"
          style={{ msOverflowStyle: 'none' }}
        >
          {/* Section 1: Thumbnail + Title/Description */}
          <div className="flex items-center gap-2 min-w-full snap-start">
            {/* Thumbnail with proper size and overlay checkbox */}
            <div className="flex-shrink-0 w-[107px] h-[60px] rounded overflow-hidden cursor-pointer relative">
              {/* Selection checkbox - overlays on thumbnail */}
              {selectable && (isHovering || selected) && (
                <SelectionCheckbox 
                  isSelected={selected} 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectionClick(e);
                  }}
                  data-selection-checkbox="true"
                  className="absolute top-2 left-2 z-30"
                />
              )}
              
              <div onClick={handleThumbnailClick}>
                <CardThumbnail 
                  thumbnailUrl={item.thumbnail_url} 
                  contentType={item.content_type}
                  title={item.title}
                  contentItem={item}
                  aspectRatio={16/9}
                />
              </div>
            </div>
            
            {/* Title/Description */}
            <div 
              className="flex-1 min-w-0 cursor-pointer px-2"
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick(e);
              }}
            >
              <h3 className="font-medium text-sm line-clamp-1 leading-tight text-card-foreground">
                {item.title}
              </h3>
              {isHighlight && highlightedText ? (
                <p className="text-xs text-muted-foreground line-clamp-1 leading-tight">
                  "{highlightedText}"
                </p>
              ) : item.description ? (
                <p className="text-xs text-muted-foreground line-clamp-1 leading-tight">
                  {item.description}
                </p>
              ) : null}
            </div>
          </div>

          {/* Section 2: Tags with Add button on right - removed scroll indicators */}
          <div className="flex items-center min-w-full snap-start px-2 gap-2">
            {/* Tags container without scroll indicators */}
            <div 
              className="flex gap-1 overflow-x-auto flex-1 scrollbar-hide"
              style={{ msOverflowStyle: 'none' }}
            >
              {contentTags.map(tag => (
                <div 
                  key={tag.id} 
                  className="flex items-center rounded-full px-2 py-1 text-xs whitespace-nowrap flex-shrink-0"
                  style={{ 
                    backgroundColor: `${tag.color}20`,
                    borderColor: tag.color,
                    border: `1px solid ${tag.color}`
                  }}
                >
                  <span className="mr-1">{tag.name}</span>
                  <button 
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full flex-shrink-0 z-10"
                    onClick={(e) => handleRemoveTag(e, tag.id)}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add Tag Button - moved to right side */}
            <button
              className="flex-shrink-0 p-1 h-8 w-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              onClick={handleAddTagClick}
            >
              <CustomAddIcon 
                size={16} 
                variant="gray" 
              />
            </button>
          </div>

          {/* Section 3: Action Buttons */}
          <div className="flex items-center justify-center min-w-full snap-start px-2 gap-2">
            {/* Pin/Unpin Button */}
            {(onPin || onUnpin) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={handlePinClick}
              >
                {item.pinned ? (
                  <PinOff className="h-4 w-4 text-gray-600" />
                ) : (
                  <Pin className="h-4 w-4 text-gray-600" />
                )}
              </Button>
            )}
            
            {/* Thumbnail Button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handleThumbnailDialogClick}
            >
              <FileImage className="h-4 w-4 text-gray-600" />
            </Button>
            
            {/* Archive Button */}
            {onArchive && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={handleArchiveClick}
              >
                <ArchiveIcon className="h-4 w-4 text-gray-600" size={16} />
              </Button>
            )}
            
            {/* Delete Button */}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRequest();
                }}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            )}
          </div>
        </div>
        
        {/* Vertical color indicator at far right */}
        <div 
          className="w-[4px] h-[60px] flex-shrink-0" 
          style={{ backgroundColor: cardColor }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div 
        ref={cardRef} 
        className="relative"
        onMouseEnter={() => setIsHovering(true)}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        data-content-card="true"
      >
        {/* Responsive layout - Desktop (lg+) vs Mobile/Tablet */}
        <div className="lg:block hidden">
          <DesktopLayout />
        </div>
        
        <div className="lg:hidden block">
          <MobileLayout />
        </div>
      </div>
      
      {/* Dialogs */}
      <GridCardDeleteDialog
        open={showDeleteDialog}
        onOpenChange={handleDeleteDialogOpenChange}
        itemTitle={item.title}
        onConfirm={handleConfirmDelete}
      />
      
      <ManualThumbnailDialog 
        contentId={item.id}
        contentTitle={item.title}
        open={showThumbnailDialog}
        onOpenChange={setShowThumbnailDialog}
      />
      
      <AddTagButton 
        contentId={item.id}
        isDialogOpen={showTagSelectionDialog}
        onDialogOpenChange={(open) => {
          setShowTagSelectionDialog(open);
          
          if (!open && showTagSelectionDialog) {
            console.log(`[NAVIGATION DEBUG] ListItemCard - Tag dialog closed, scheduling refresh`);
            setTimeout(() => {
              console.log(`[NAVIGATION DEBUG] ListItemCard - Forcing page refresh after tag dialog close`);
              window.location.reload();
            }, 300);
          }
        }}
        className="hidden"
      />
    </>
  );
};

export default ListItemCard;
